import { useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useBrandManager } from '@/hooks/useBrandManager';
import { COMPLETE_MENU_STRUCTURE, type MenuNode } from '@/config/menuStructure';

interface MenuItemMatch {
  id: string;
  path: string;
  label: string;
}

// Flatten menu structure to find matching items
const flattenMenuItems = (nodes: MenuNode[], result: MenuItemMatch[] = []): MenuItemMatch[] => {
  for (const node of nodes) {
    if (node.path) {
      result.push({ id: node.id, path: node.path, label: node.label });
    }
    if (node.children) {
      flattenMenuItems(node.children, result);
    }
  }
  return result;
};

const allMenuItems = flattenMenuItems(COMPLETE_MENU_STRUCTURE);

// Find the best matching menu item for a given path
const findMatchingMenuItem = (pathname: string): MenuItemMatch | null => {
  // First try exact match
  const exactMatch = allMenuItems.find(item => item.path === pathname);
  if (exactMatch) return exactMatch;

  // Try prefix match for dynamic routes (e.g., /brainstorming-ideas/123 -> /brainstorming-ideas)
  const sortedByLength = [...allMenuItems].sort((a, b) => b.path.length - a.path.length);
  for (const item of sortedByLength) {
    if (pathname.startsWith(item.path + '/') || pathname.startsWith(item.path + '?')) {
      return item;
    }
  }

  return null;
};

export const useNavigationTracking = () => {
  const location = useLocation();
  const { currentBrandId } = useBrandManager();
  const lastTrackedPath = useRef<string>('');
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);

  const trackNavigation = useCallback(async (pathname: string) => {
    if (!currentBrandId) return;

    const menuItem = findMatchingMenuItem(pathname);
    if (!menuItem) return;

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // First, check if record exists
      const { data: existing } = await supabase
        .from('os_navigation_usage')
        .select('id, click_count')
        .eq('user_id', user.id)
        .eq('brand_id', currentBrandId)
        .eq('menu_item_id', menuItem.id)
        .maybeSingle();

      if (existing) {
        // Update existing record - increment click count
        await supabase
          .from('os_navigation_usage')
          .update({
            click_count: existing.click_count + 1,
            last_accessed_at: new Date().toISOString(),
            menu_label: menuItem.label,
          })
          .eq('id', existing.id);
      } else {
        // Insert new record
        await supabase
          .from('os_navigation_usage')
          .insert({
            user_id: user.id,
            brand_id: currentBrandId,
            menu_item_id: menuItem.id,
            menu_path: menuItem.path,
            menu_label: menuItem.label,
            click_count: 1,
            last_accessed_at: new Date().toISOString(),
          });
      }
    } catch (err) {
      console.error('Error tracking navigation:', err);
    }
  }, [currentBrandId]);

  useEffect(() => {
    const pathname = location.pathname;

    // Skip if same path or no brand
    if (pathname === lastTrackedPath.current || !currentBrandId) return;

    // Debounce to avoid multiple rapid calls
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(() => {
      lastTrackedPath.current = pathname;
      trackNavigation(pathname);
    }, 500);

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [location.pathname, currentBrandId, trackNavigation]);
};
